<template>
  <div :class="reviewReplyClasses">
    <div v-if="replyText" class="review-reply__last-reply">
      <header class="review-reply__header">
        <div class="review-reply__agent">
          <strong>
            <span v-if="review.reply && review.reply.agentEmail">
              {{ $t('APP.REVIEWS.REPLY_BY') }}
              {{ review.reply.agentEmail }}:
            </span>
            <span v-else>{{ $t('APP.REVIEWS.REPLY') }}:</span>
          </strong>
        </div>
        <div class="review-reply__status-message">
          <span class="review-reply__status">
            <strong>{{ $t('APP.REVIEWS.REPLY_STATUS') }}:</strong>
            {{ auditStateText }}
          </span>
        </div>
      </header>
      <span>
        {{ replyText }}
      </span>
    </div>

    <div v-show="showReplyBox" class="review-reply__form-wrapper">
      <hr v-show="replyText" class="divider" />

      <ReviewReplyForm
        :review-id="review.id"
        :is-submitting="isSubmitting"
        @submit-reply="replyToReview"
        @cancel-reply="cancelReply"
      />
    </div>
  </div>
</template>

<script>
import Reviews from '@/api/app-reviews';
import ReviewReplyForm from './ReviewReplyForm';
import packageName from '@/mixins/packageName';

export default {
  name: 'ReviewReply',

  components: { ReviewReplyForm },

  mixins: [packageName],

  props: {
    showReplyBox: {
      type: Boolean,
      default: false,
    },
    review: {
      type: Object,
      required: true,
    },
  },

  data() {
    return {
      reply: '',
      isSubmitting: false,
    };
  },

  computed: {
    replyText() {
      if (this.reply) {
        return this.reply;
      }
      return this.review.reply && this.review.reply.text;
    },

    reviewReplyClasses() {
      const hasReply = !!this.review.reply?.text;
      const replyAuditState = this.review.reply?.auditState;
      const base = 'review-reply';

      if (this.showReplyBox) {
        return [base, 'review-reply--replying'];
      } else if (!hasReply) {
        return [base, 'review-reply--no-reply'];
      }

      const auditClasses = {
        0: 'review-reply--published',
        1: 'review-reply--pending',
        2: 'review-reply--rejected',
        3: 'review-reply--removed',
      };

      const stateClass = auditClasses[replyAuditState] || '';

      return [base, stateClass];
    },

    auditStateText() {
      const auditState = this.review.reply?.auditState;
      switch (auditState) {
        case 0:
          return this.$t('APP.REVIEWS.REPLY_STATUS.PUBLISHED');
        case 1:
          return this.$t('APP.REVIEWS.REPLY_STATUS.PENDING');
        case 2:
          return this.$t('APP.REVIEWS.REPLY_STATUS.REJECTED');
        case 3:
          return this.$t('APP.REVIEWS.REPLY_STATUS.REMOVED');
        default:
          return '';
      }
    },
  },

  methods: {
    replyToReview(reply, done) {
      this.isSubmitting = true;

      return Reviews.replyToReview({
        packageName: this.packageName,
        id: this.review.id,
        text: reply,
      })
        .then(message => {
          this.reply = reply;
          this.$message.success(message);
          done();
        })
        .catch(({ message }) => {
          this.$message.error(message);
        })
        .finally(() => {
          this.isSubmitting = false;
        });
    },

    cancelReply() {
      this.$emit('cancel-reply');
    },
  },
};
</script>

<style lang="scss">
.review-reply {
  background: rgba($clouds, 0.8);
  padding: $global-padding;
  margin-bottom: $global-margin;

  &--no-reply {
    padding: 0;
    background: transparent;
  }

  &--published {
    background: rgba($success-color, 0.1);
    border-left: 4px solid $success-color;
  }

  &--pending {
    background: rgba($warning-color, 0.1);
    border-left: 4px solid $warning-color;
  }

  &--rejected {
    background: rgba($alert-color, 0.1);
    border-left: 4px solid $alert-color;
  }

  &--removed {
    background: rgba(darken($alert-color, 20%), 0.1);
    border-left: 4px solid darken($alert-color, 20%);
  }

  &__header {
    display: flex;
    flex-wrap: wrap;
    justify-content: space-between;

    @include breakpoint(medium) {
      flex-wrap: nowrap;
    }
  }

  &__status {
    font-size: 12px;
    color: $dark-gray;
  }

  &--published &__status {
    color: darken($success-color, 20%);
  }

  &--pending &__status {
    color: darken($warning-color, 20%);
  }

  &--rejected &__status {
    color: darken($alert-color, 10%);
  }

  &--removed &__status {
    color: darken($alert-color, 30%);
  }

  &__form-wrapper {
    background: transparent;
    padding: 0;
    margin-top: $global-margin;
  }
}
</style>
